import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Lock, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const hasCoaching = user?.has_coaching === true || user?.role === "admin";

  const load = () => api.get("/messages").then(r => setMsgs(r.data));
  useEffect(() => { if (hasCoaching) load(); }, [hasCoaching]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/messages", { body: text.trim() });
      setText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    setBusy(false);
  };

  if (!hasCoaching) {
    return (
      <AppShell>
        <div data-testid="messages-locked" className="max-w-xl mx-auto py-10">
          <div className="bg-[#0a0a0a] border border-[#d7ff00]/40 p-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#d7ff00]/8 to-transparent pointer-events-none" />
            <div className="relative">
              <div className="w-14 h-14 border border-[#d7ff00]/50 flex items-center justify-center mb-5">
                <Lock className="w-6 h-6 text-[#d7ff00]" />
              </div>
              <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> COACHING REQUIRED
              </div>
              <h1 className="mt-2 font-display text-3xl sm:text-4xl uppercase leading-tight">Unlock direct chat with <span className="text-[#d7ff00]">Coach JP</span></h1>
              <p className="mt-4 text-[#bbb] leading-relaxed">
                Upgrade to Coaching to receive direct feedback, training adjustments and support from Coach JP.
              </p>

              <ul className="mt-6 space-y-2.5 text-sm text-[#ddd]">
                {[
                  "Direct messaging — text Coach JP whenever you need",
                  "Training plan adjustments based on your feedback",
                  "Race strategy & pacing guidance",
                  "Priority response within 24h",
                ].map(b => (
                  <li key={b} className="flex items-start gap-2.5">
                    <MessageCircle className="w-4 h-4 text-[#d7ff00] mt-0.5 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              <Link
                to="/packages"
                className="mt-7 inline-flex items-center gap-2 bg-[#d7ff00] text-black font-display tracking-wider uppercase px-5 py-3 hover:bg-white"
                data-testid="upgrade-coaching-cta"
              >
                Add Coaching · RM 199 / month
              </Link>
              <p className="mt-3 text-xs text-[#666] font-mono-jp uppercase tracking-widest">Cancel anytime — your program & history stay yours.</p>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div data-testid="messages-page" className="flex flex-col h-[calc(100vh-180px)]">
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="Coach JP" className="w-12 h-12 rounded-full border border-[#d7ff00]/40 object-cover" />
          <div>
            <div className="font-display text-2xl uppercase">Coach JP</div>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Direct messaging · responds within 24h</div>
          </div>
        </div>

        <div className="mt-4 flex-1 overflow-y-auto bg-[#0a0a0a] border border-[#1f1f1f] p-4 space-y-3" data-testid="messages-list">
          {msgs.length === 0 && (
            <div className="text-center text-[#888] text-sm py-12">
              <div className="font-display text-2xl uppercase text-[#d7ff00] mb-2">Send Coach JP a message</div>
              Questions about training, recovery, pacing or race strategy — Coach JP reads and replies personally.
            </div>
          )}
          {msgs.map(m => (
            <div key={m.id} className={`flex ${m.from_role === "athlete" ? "justify-end" : "justify-start"}`}>
              {m.from_role !== "athlete" && <img src="/icon-192.png" alt="JP" className="w-7 h-7 rounded-full border border-[#d7ff00]/40 object-cover mr-2 flex-shrink-0" />}
              <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${m.from_role === "athlete" ? "bg-[#d7ff00] text-black" : "bg-[#161616] text-[#eee] border border-[#222]"}`}>
                {m.body}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="mt-3 flex gap-2">
          <input value={text} onChange={e => setText(e.target.value)} placeholder="Message Coach JP…" disabled={busy}
            className="flex-1 bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-3 text-white" data-testid="message-input" />
          <button type="submit" disabled={busy || !text.trim()} className="bg-[#d7ff00] text-black px-5 hover:bg-white disabled:opacity-40" data-testid="message-send">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </AppShell>
  );
}
